import { useState } from 'react';
import type { CSSProperties } from 'react';
import { Pause, Play, Sun } from '@phosphor-icons/react';
import { site } from '../content/site';
import { useReducedMotion } from '../lib/useReducedMotion';

const TITLE_ID = 'ce-que-j-automatise';

// Accessible names of the pause control (candidates for site.marquee in site.ts).
const PAUSE_LABEL = site.marquee.pause;
const PLAY_LABEL = site.marquee.play;

/**
 * Full-bleed grid whose first column ends exactly where .container-site content
 * starts (max width 1400px, same fluid padding). The `100%` inside --edge resolves
 * in grid-template-columns, i.e. against this element's width.
 */
const FRAME = {
	'--pad': 'clamp(1.25rem, 4vw, 3rem)',
	'--edge': 'max(var(--pad), calc((100% - 1400px) / 2 + var(--pad)))',
} as CSSProperties;

/** Soft entry and exit of the moving words (the track is clipped, never the page). */
const FADE = 'linear-gradient(to right, transparent, #000 7%, #000 93%, transparent)';
const EDGE_FADE: CSSProperties = { maskImage: FADE, WebkitMaskImage: FADE };

const ITEM = 'font-display font-medium text-graphite dark:text-mineral';

/**
 * The page's only marquee: the concrete chores Ozan automates, between the proof
 * (Work) and the offer (Expertise). Breadth without demanding attention. The list
 * is rendered twice for a seamless -50% loop, the copy is hidden from assistive
 * tech. It pauses on hover and with its own button (WCAG 2.2.2); with reduced
 * motion it becomes a static wrapped list.
 */
export function Marquee() {
	const reduced = useReducedMotion();
	const [paused, setPaused] = useState(false);
	const items = site.marquee.items;

	return (
		<section aria-labelledby={TITLE_ID} className="relative py-16 md:py-24">
			<div
				style={FRAME}
				className="grid items-center gap-y-7 lg:grid-cols-[var(--edge)_auto_minmax(0,1fr)]"
			>
				<div className="flex items-center justify-between gap-6 px-[var(--pad)] lg:col-start-2 lg:flex-col lg:items-start lg:gap-5 lg:px-0 lg:pr-12">
					<h2
						id={TITLE_ID}
						className="font-display text-xl font-bold leading-[1.1] tracking-[-0.02em] text-graphite dark:text-mineral lg:max-w-[7em]"
					>
						{site.marquee.label}
					</h2>
					{!reduced && (
						<button
							type="button"
							onClick={() => setPaused((value) => !value)}
							aria-label={paused ? PLAY_LABEL : PAUSE_LABEL}
							className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-graphite/20 text-graphite transition-[border-color,background-color,transform] duration-300 ease-expo hover:border-graphite/50 hover:bg-graphite/[0.04] active:scale-[0.96] dark:border-mineral/20 dark:text-mineral dark:hover:border-mineral/50 dark:hover:bg-mineral/[0.06]"
						>
							{paused ? <Play aria-hidden size={14} weight="fill" /> : <Pause aria-hidden size={14} weight="fill" />}
						</button>
					)}
				</div>

				{reduced ? (
					<ul className="flex flex-wrap items-center gap-y-1 px-[var(--pad)] lg:col-start-3 lg:pl-0">
						{items.map((item, index) => (
							<li
								key={item}
								className={`${ITEM} flex items-center text-[clamp(1.5rem,3vw,2.5rem)] leading-[1.2] tracking-[-0.02em]`}
							>
								{item}
								{index < items.length - 1 && (
									<Sun aria-hidden size="0.42em" className="mx-[0.45em] shrink-0 text-forest dark:text-solar" />
								)}
							</li>
						))}
					</ul>
				) : (
					<div className="overflow-hidden lg:col-start-3" style={EDGE_FADE}>
						<div
							className="flex w-max animate-marquee [--marquee-duration:46s] hover:[animation-play-state:paused] lg:[--marquee-duration:64s]"
							style={paused ? { animationPlayState: 'paused' } : undefined}
						>
							{[0, 1].map((copy) => (
								<ul
									key={copy}
									aria-hidden={copy === 1 ? true : undefined}
									className="flex shrink-0 items-center"
								>
									{items.map((item) => (
										<li
											key={item}
											className={`${ITEM} flex items-center whitespace-nowrap text-[clamp(2rem,4.6vw,4.5rem)] leading-[1.15] tracking-[-0.03em]`}
										>
											{item}
											<Sun aria-hidden size="0.42em" className="mx-[0.55em] shrink-0 text-forest dark:text-solar" />
										</li>
									))}
								</ul>
							))}
						</div>
					</div>
				)}
			</div>
		</section>
	);
}
